import { useEffect, useState }        from "react";
import { useNavigate }                 from "react-router-dom";
import useAuthStatus                   from "../../hooks/useAuthStatus";
import MobileBottomNav                 from "../../components/feature/MobileBottomNav";
import { apiClient, getUser }                     from "../../api/apiServices";
import { useAuth }                     from "../../context/useAuth";
import { useAnalytics }                from "../../hooks/useAnalytics";
import PageLoader                      from "../../components/PageLoader";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import { useTheme } from "../../context/ThemeContext";

// KYC tiers now reference semantic tones (success/info/warning/danger/muted)
// instead of hardcoded hex, so the chip background and icon color both
// flip correctly under dark mode via the existing -light token variants.
const KYC_TIER = {
  not_started:    { label: "Unverified",   tone: "muted",   icon: "ri-shield-line",      limit: "₦50,000/day"    },
  submitted:      { label: "Under Review", tone: "info",    icon: "ri-time-line",         limit: "₦50,000/day"    },
  under_review:   { label: "Under Review", tone: "info",    icon: "ri-time-line",         limit: "₦50,000/day"    },
  info_requested: { label: "Info Needed",  tone: "warning", icon: "ri-information-line",  limit: "₦50,000/day"    },
  rejected:       { label: "Rejected",     tone: "danger",  icon: "ri-shield-cross-line", limit: "₦50,000/day"    },
  approved:       { label: "Verified",     tone: "success", icon: "ri-shield-check-line", limit: "₦5,000,000/day" },
};

// Resolves a tone name to its base/-light/text CSS vars. "muted" isn't one
// of the 5 semantic tones in tokens.css, so it falls back to the neutral
// text-muted / surface-muted pair instead of a colored chip.
function toneVars(tone) {
  if (tone === "muted") {
    return { color: "var(--color-text-muted)", bg: "var(--color-surface-muted)", ring: "transparent" };
  }
  return { color: `var(--color-${tone})`, bg: `var(--color-${tone}-light)`, ring: `var(--color-${tone}-ring)` };
}

function SectionHeader({ label }) {
  return (
    <p className="text-muted text-[11px] font-bold uppercase tracking-widest px-1 mb-2">
      {label}
    </p>
  );
}

function MenuRow({ icon, label, sub, tone = "muted", badge, badgeTone, chevron = true, onClick, danger }) {
  const t = toneVars(danger ? "danger" : tone);
  const badgeT = badge ? toneVars(badgeTone || tone) : null;
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-muted active:bg-surface-hover transition-colors">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.bg, boxShadow: `inset 0 0 0 1px ${t.ring}` }}>
        <i className={`${icon} text-base`} style={{ color: t.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? "text-danger" : "text-primary"}`}>{label}</p>
        {sub && <p className="text-muted text-xs mt-0.5">{sub}</p>}
      </div>
      {badge && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ background: badgeT.bg, color: badgeT.color }}>
          {badge}
        </span>
      )}
      {chevron && !badge && <i className="ri-arrow-right-s-line text-muted text-lg flex-shrink-0" />}
    </button>
  );
}

function GuestProfile({ onSignIn }) {
  const perks = [
    { icon: "ri-shield-user-line",    tone: "success", title: "KYC Verification",    desc: "Unlock higher limits and full access" },
    { icon: "ri-gift-line",           tone: "warning", title: "Rewards & Referrals", desc: "Earn cashback and refer friends"      },
    { icon: "ri-bank-line",           tone: "info",    title: "Saved Bank Accounts", desc: "Save banks for instant withdrawals"   },
    { icon: "ri-lock-password-line",  tone: "purple",  title: "Transaction PIN",     desc: "Protect every payment with a PIN"     },
  ];
  return (
    <div className="px-5 pb-8 space-y-6 pt-6">
      <div className="text-center">
        <div className="w-20 h-20 bg-surface-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-user-line text-muted text-3xl" />
        </div>
        <h2 className="text-primary font-bold text-xl mb-1 font-display">
          Your Profile
        </h2>
        <p className="text-secondary text-sm">Sign in to manage your account</p>
      </div>
      <div className="space-y-3">
        {perks.map(p => (
          <div key={p.title} className="flex items-center gap-3 bg-surface rounded-2xl px-4 py-3.5 border border-border shadow-card">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${p.tone}-light)` }}>
              <i className={`${p.icon} text-base`} style={{ color: `var(--color-${p.tone})` }} />
            </div>
            <div>
              <p className="text-primary text-sm font-semibold">{p.title}</p>
              <p className="text-muted text-xs">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <button onClick={onSignIn}
          className="w-full py-4 bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2">
          <i className="ri-login-box-line" />Sign In
        </button>
        <button onClick={onSignIn}
          className="w-full py-3.5 bg-surface border border-border-strong text-secondary font-semibold text-sm rounded-2xl hover:bg-surface-hover transition-all active:scale-95 flex items-center justify-center gap-2">
          <i className="ri-user-add-line text-brand" />Create Account
        </button>
      </div>
    </div>
  );
}


function VirtualAccountCard({ user }) {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(null);

  const isKycApproved = user?.kycStatus === "approved" || user?.kycVerified;

  useEffect(() => {
    if (!isKycApproved) { setLoading(false); return; }
    apiClient.get("/mtrade/wallet/virtual-account")
      .then(r => { if (r.data.status) setAccount(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isKycApproved]);

  const handleCopy = async (value, key) => {
    await navigator.clipboard.writeText(value).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  // KYC not approved — prompt
  if (!isKycApproved) {
    return (
      <div className="mx-4 mb-3 flex items-center gap-3 px-4 py-3 bg-warning-light border border-border rounded-2xl">
        <i className="ri-shield-user-line text-warning text-base flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-warning text-xs font-semibold">No bank account — KYC required</p>
          <p className="text-muted text-[10px]">Complete KYC to get a dedicated account number</p>
        </div>
        <button onClick={() => navigate("/kyc")}
          className="text-[10px] font-bold px-2.5 py-1.5 rounded-xl bg-warning text-on-brand flex-shrink-0">
          Verify
        </button>
      </div>
    );
  }

  // Loading
  if (loading) {
    return <div className="mx-4 mb-3 h-20 bg-surface-hover rounded-2xl animate-pulse" />;
  }

  // No account yet
  if (!account) {
    return (
      <div className="mx-4 mb-3 flex items-center gap-3 px-4 py-3 bg-surface-muted border border-border shadow-card rounded-2xl">
        <i className="ri-bank-line text-muted text-base flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-secondary text-xs font-semibold">No virtual account yet</p>
          <p className="text-muted text-[10px]">Generate a dedicated account number for transfers</p>
        </div>
        <button onClick={() => navigate("/deposit-fiat?tab=transfer")}
          className="text-[10px] font-bold px-2.5 py-1.5 rounded-xl bg-brand text-on-brand flex-shrink-0">
          Generate
        </button>
      </div>
    );
  }

  // Account exists — show inline card. Uses the same --gradient-hero as
  // Home.jsx's balance card instead of a separately-maintained near-
  // duplicate gradient, so the "signature" surface stays consistent
  // across pages in both light and dark mode.
  return (
    <div className="mx-4 mb-3 rounded-2xl overflow-hidden relative" style={{ background: "var(--gradient-hero)", boxShadow: "var(--card-top-highlight)" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />
      <div className="px-4 py-4 relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-bank-line text-white/60 text-sm" />
            <p className="text-white/60 text-xs">{account.bankName}</p>
          </div>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/80">
            Your Account
          </span>
        </div>

        {/* Account number */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/40 text-[9px] mb-0.5">Account Number</p>
            <p className="text-white font-bold tracking-widest text-lg font-display" style={{ letterSpacing: "0.15em" }}>
              {account.accountNumber}
            </p>
          </div>
          <button onClick={() => handleCopy(account.accountNumber, "number")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-all">
            <i className={`${copied === "number" ? "ri-check-line text-emerald-400" : "ri-file-copy-line"} text-sm`} />
            {copied === "number" ? "Copied!" : "Copy"}
          </button>
        </div>

        {/* Account name */}
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-white/50 text-xs">{account.accountName}</p>
          <button onClick={() => handleCopy(account.accountName, "name")}
            className="text-white/30 text-[10px] hover:text-white/50 transition-colors">
            {copied === "name" ? "Copied!" : "Copy name"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();
  const { track }       = useAnalytics();
  const { logout }      = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [user,        setUser]        = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) track.pageView("/profile", "Profile");
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { setUserLoading(false); return; }
    getUser()
      .then(data => { if (data?.status) setUser(data.user); })
      .catch(err => console.error(err?.response?.data?.message))
      .finally(() => setUserLoading(false));
  }, [isAuthenticated]);

  const { fees, limits } = useTradingConfig({ enabled: isAuthenticated });
  const refReward       = fees.rewardOnReferral || 20;

  const handleLogout = async () => {
    try { await logout(); navigate("/"); } catch {}
  };

  if (userLoading) return <PageLoader size="md" message="Loading profile…" />;

  const kycStatus  = user?.kycStatus || "not_started";
  const tier       = KYC_TIER[kycStatus] || KYC_TIER.not_started;
  const tierVars   = toneVars(tier.tone);
  const isVerified = kycStatus === "approved";

  return (
    <div className="min-h-screen bg-bg pb-28">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border flex items-center justify-between">
        <h1 className="text-primary font-bold text-xl font-display">
          Profile & Settings
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="w-9 h-9 rounded-xl bg-surface-muted border border-border shadow-card flex items-center justify-center transition-colors hover:bg-surface-hover">
            <i className={`${theme === "dark" ? "ri-sun-line text-warning" : "ri-moon-line text-secondary"} text-lg`} />
          </button>
          <button onClick={() => navigate("/support")} className="w-9 h-9 rounded-xl bg-warning-light flex items-center justify-center">
            <i className="ri-customer-service-2-line text-warning text-lg" />
          </button>
        </div>
      </div>

      {!isAuthenticated ? (
        <GuestProfile onSignIn={() => navigate("/auth")} />
      ) : (
        <div className="px-5 pt-4 space-y-5">

          {/* User card */}
          <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden">
            <div className="px-5 py-5 flex items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-brand-light border-2 border-brand-border flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {user?.profileImage
                    ? <img src={user.profileImage} className="w-full h-full object-cover" alt="" />
                    : <span className="text-brand-text font-bold text-2xl">{user?.firstName?.charAt(0) || "?"}</span>
                  }
                </div>
                {isVerified && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-success border-2 border-surface flex items-center justify-center">
                    <i className="ri-check-line text-white text-[9px]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-primary font-bold text-base truncate font-display">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-muted text-xs truncate">{user?.email}</p>
                {user?.phone && <p className="text-muted text-xs">{user.phone}</p>}
              </div>
              <button onClick={() => navigate("/profile/personal")}
                className="w-9 h-9 rounded-xl bg-surface-muted hover:bg-surface-hover flex items-center justify-center flex-shrink-0 transition-colors">
                <i className="ri-edit-line text-secondary text-base" />
              </button>
            </div>

            {/* KYC tier bar */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-border" style={{ background: tierVars.bg }}>
              <div className="flex items-center gap-2">
                <i className={`${tier.icon} text-sm`} style={{ color: tierVars.color }} />
                <span className="text-xs font-bold" style={{ color: tierVars.color }}>{tier.label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-arrow-up-circle-line text-muted text-xs" />
                <span className="text-secondary text-xs">{tier.limit}</span>
              </div>
            </div>
          </div>

          {/* Referral card — uses the same brand surface treatment as
              buttons elsewhere instead of a separately hardcoded gradient */}
          {user?.referralCode && (
            <div className="rounded-2xl px-4 py-4 relative overflow-hidden" style={{ background: "var(--gradient-hero)", boxShadow: "var(--card-top-highlight)" }}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />
              <div className="flex items-center justify-between mb-3 relative z-10">
                <div>
                  <p className="text-white text-sm font-bold font-display">
                    Refer & Earn
                  </p>
                  <p className="text-white/60 text-xs mt-0.5">₦{refReward} per referral who completes KYC</p>
                </div>
                <i className="ri-gift-line text-white text-2xl" />
              </div>
              <div className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2 relative z-10">
                <span className="text-white font-mono text-sm font-bold flex-1">{user.referralCode}</span>
                <button onClick={() => navigator.clipboard.writeText(user.referralCode)}
                  className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg">
                  <i className="ri-file-copy-line text-white text-sm" />
                </button>
                <button onClick={() => navigator.share?.({ title: "Join Easwallet", text: `Use my code ${user.referralCode}!` })}
                  className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg">
                  <i className="ri-share-line text-white text-sm" />
                </button>
              </div>
            </div>
          )}

          {/* ── Profile ── */}
          <div>
            <SectionHeader label="Profile" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">

              {/* Virtual account — shown inline at the top of this section */}
              <VirtualAccountCard user={user} />
              <MenuRow
                icon="ri-user-3-line" label="Personal Information"
                sub={`${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Update your details"}
                tone="success"
                onClick={() => navigate("/profile/personal")}
              />
              <MenuRow
                icon="ri-shield-keyhole-line" label="KYC Verification"
                sub={isVerified ? "Your identity is verified" : "Complete to unlock full access"}
                tone={tier.tone}
                badge={tier.label}
                badgeTone={tier.tone}
                onClick={() => navigate("/kyc")}
              />
            </div>
          </div>

          {/* ── Security ── */}
          <div>
            <SectionHeader label="Security" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-lock-password-line" label="Transaction PIN"
                sub={user?.hasTransactionPin ? "PIN is set — tap to change" : "Set a PIN to confirm payments"}
                tone="purple"
                badge={!user?.hasTransactionPin ? "Required" : undefined}
                badgeTone="danger"
                onClick={() => navigate(user?.hasTransactionPin ? "/change-pin" : "/create-pin")}
              />
            </div>
          </div>

          {/* ── Wallet & Payments ── */}
          <div>
            <SectionHeader label="Wallet & Payments" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-bank-line" label="Saved Bank Accounts"
                sub="Manage withdrawal destinations"
                tone="info"
                onClick={() => navigate("/saved-banks")}
              />
              <MenuRow
                icon="ri-exchange-line" label="Transaction History"
                sub="View all past transactions"
                tone="success"
                onClick={() => navigate("/transactions")}
              />
            </div>
          </div>

          {/* ── Rewards ── */}
          <div>
            <SectionHeader label="Rewards" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-medal-line" label="Cashback & Milestones"
                sub="View your earned rewards"
                tone="warning"
                onClick={() => navigate("/rewards")}
              />
              {/* Referral tracking goes to rewards — /referrals page not built yet */}
              <MenuRow
                icon="ri-team-line" label="Referral Programme"
                sub="Track referrals and earnings"
                tone="success"
                onClick={() => navigate("/rewards")}
              />
            </div>
          </div>

          {/* ── Preferences ── */}
          <div>
            <SectionHeader label="Preferences" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-notification-3-line" label="Notifications"
                sub="Push, email and SMS alerts"
                tone="warning"
                onClick={() => navigate("/profile/notifications")}
              />
              <MenuRow
                icon={theme === "dark" ? "ri-sun-line" : "ri-moon-line"}
                label="Appearance"
                sub={theme === "dark" ? "Dark mode is on" : "Light mode is on"}
                tone="info"
                badge={theme === "dark" ? "Dark" : "Light"}
                badgeTone="info"
                onClick={toggleTheme}
              />
            </div>
          </div>

          {/* ── Support ── */}
          <div>
            <SectionHeader label="Support" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-customer-service-2-line" label="Help & Support"
                sub="FAQs and contact our team"
                tone="warning"
                onClick={() => navigate("/support")}
              />
              <MenuRow
                icon="ri-bug-line" label="Report a Problem"
                sub="Tell us what went wrong"
                tone="danger"
                onClick={() => navigate("/support?tab=report")}
              />
            </div>
          </div>

          {/* ── Legal ── */}
          <div>
            <SectionHeader label="Legal" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden divide-y divide-border">
              <MenuRow
                icon="ri-file-text-line" label="Terms of Service"
                tone="muted"
                onClick={() => navigate("/terms-privacy?tab=terms")}
              />
              <MenuRow
                icon="ri-shield-line" label="Privacy Policy"
                tone="muted"
                onClick={() => navigate("/terms-privacy?tab=privacy")}
              />
              <MenuRow
                icon="ri-information-line" label="App Version"
                sub="v1.0.0"
                tone="muted"
                chevron={false}
                onClick={() => {}}
              />
            </div>
          </div>

          {/* ── Sign out ── */}
          <div>
            <SectionHeader label="Account" />
            <div className="bg-surface border border-border shadow-card rounded-2xl overflow-hidden">
              <MenuRow
                icon="ri-logout-box-r-line" label="Sign Out"
                tone="danger" danger
                onClick={handleLogout}
              />
            </div>
          </div>

          <div className="h-4" />
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}
